<template>
    <div class="auth-step">
        <button type="button" class="auth-step__back" @click="$emit('back')">
            <v-icon icon="mdi-arrow-left" size="18" class="auth-step__back-icon" />
            Back
        </button>

        <h1 class="app-typography-title-1 auth-step__title">Verify OTP to continue</h1>
        <p class="auth-step__subtitle">
            We sent an OTP to <span class="auth-step__email">{{ email }}</span>
        </p>

        <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-4 rounded-lg">
            {{ errorMsg }}
        </v-alert>

        <div class="auth-step__otp-wrap">
            <v-otp-input
                v-model="otp"
                length="6"
                variant="outlined"
                :disabled="loading"
                @finish="onFinish"
            />
        </div>

        <button
            type="button"
            class="auth-step__resend mb-6"
            :class="{ 'auth-step__resend--disabled': !canResend }"
            :disabled="loading || !canResend"
            :aria-disabled="loading || !canResend"
            @click="onResend"
        >
            {{ resendLabel }}
        </button>

        <v-btn
            block
            color="primary"
            class="text-none app-btn-primary"
            :loading="loading"
            elevation="0"
            @click="onFinish"
        >
            Verify
        </v-btn>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = defineProps<{
    errorMsg?: string
    loading?: boolean
    email: string
    canResend: boolean
    resendLabel: string
}>()

const emit = defineEmits<{
    verifyOtp: [otp: string]
    back: []
    resend: []
}>()

const otp = ref('')

function onFinish() {
    if (otp.value.length === 6) {
        emit('verifyOtp', otp.value)
    }
}

function onResend() {
    if (!props.loading && props.canResend) {
        emit('resend')
    }
}
</script>

<style scoped>
.auth-step__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0 0 20px;
    padding: 0;
    border: none;
    background: transparent;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
}

.auth-step__back-icon {
    color: rgb(var(--v-theme-primary));
}

.auth-step__title {
    margin: 0 0 8px;
}

.auth-step__subtitle {
    margin: 0 0 24px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: rgb(var(--v-theme-neutral));
}

.auth-step__email {
    font-weight: 600;
    color: rgb(var(--v-theme-heading));
}

.auth-step__otp-wrap {
    margin-bottom: 12px;
}

.auth-step__otp-wrap :deep(.v-otp-input__content) {
    padding: 0 !important;
    gap: 12px;
    max-width: none !important;
    width: 100%;
    justify-content: space-between;
}

.auth-step__otp-wrap :deep(.v-otp-input .v-field) {
    border-radius: 8px;
    width: 48px !important;
    height: 48px !important;
    flex: 0 0 48px !important;
    margin: 0 !important;
}

.auth-step__resend {
    display: block;
    padding: 0;
    border: none;
    background: transparent;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-primary));
    text-decoration: underline;
    cursor: pointer;
}

.auth-step__resend--disabled,
.auth-step__resend:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    text-decoration: none;
}
</style>
